import { ChevronDown, Link2, Paperclip } from 'lucide-react';
import { useRef, useState } from 'react';

import { Button } from '../Button';
import { Input } from '../Input';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../Tooltip';
import { BLOCK_ACTIONS, BLOCK_OPTIONS, LIST_ACTIONS, MARK_ACTIONS } from './constants';
import { withProtocol } from './helpers';
import type { RichTextAction, RichTextTipProps, RichTextToolbarProps } from './types';

const TOOL =
  'inline-flex h-7 min-w-7 items-center justify-center gap-0.5 rounded px-1 text-xs text-text-tertiary transition-colors hover:bg-chip-hover hover:text-text-primary aria-pressed:bg-chip-selected-bg aria-pressed:text-text-emphasis focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus [&_svg]:size-4';

// Keeping the pointer from taking focus is what lets a click format the text
// that was selected when it started.
const keepSelection = (e: React.MouseEvent) => e.preventDefault();

// The editor sits inside dialogs, so its tips stack on the popover layer rather
// than the tooltip default, which would land under the dialog.
function Tip({ tip, shortcut, children }: RichTextTipProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent className="px-1.5 py-1 text-[11px] leading-3">
        {tip}
        {shortcut && <span className="ml-1.5 opacity-70">{shortcut}</span>}
      </TooltipContent>
    </Tooltip>
  );
}

function ToolButton({ action, editor }: { action: RichTextAction } & RichTextToolbarProps) {
  const Icon = action.icon;
  return (
    <Tip tip={action.tip} shortcut={action.shortcut}>
      <button
        type="button"
        aria-label={action.label}
        aria-pressed={action.isActive(editor)}
        onMouseDown={keepSelection}
        onClick={() => action.run(editor)}
        className={TOOL}
      >
        <Icon />
      </button>
    </Tip>
  );
}

function BlockTypeMenu({ editor }: RichTextToolbarProps) {
  const [open, setOpen] = useState(false);
  const current =
    BLOCK_OPTIONS.find((o) => o.level !== null && editor.isActive('heading', { level: o.level })) ??
    BLOCK_OPTIONS[0];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tip tip="Switch between normal text and headings">
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Text style"
            onMouseDown={keepSelection}
            className={`${TOOL} w-14 justify-between`}
          >
            {current.short} <ChevronDown className="!size-3" />
          </button>
        </PopoverTrigger>
      </Tip>
      <PopoverContent
        align="start"
        className="w-40 p-1"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {BLOCK_OPTIONS.map((o) => (
          <button
            key={o.label}
            type="button"
            onMouseDown={keepSelection}
            onClick={() => {
              const chain = editor.chain().focus();
              if (o.level === null) chain.setParagraph().run();
              else chain.setHeading({ level: o.level }).run();
              setOpen(false);
            }}
            className="flex w-full rounded px-2 py-1.5 text-left text-sm hover:bg-chip-hover"
          >
            {o.label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function LinkMenu({ editor }: RichTextToolbarProps) {
  const [open, setOpen] = useState(false);
  const [href, setHref] = useState('');
  const active = editor.isActive('link');

  const apply = () => {
    const typed = href.trim();
    const url = withProtocol(typed);
    if (typed === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else if (editor.state.selection.empty && !active) {
      // With nothing to wrap, the address itself becomes the linked text. A link
      // grows with whatever is typed at its end, so a plain space follows it to
      // leave the cursor outside.
      editor
        .chain()
        .focus()
        .insertContent([
          { type: 'text', text: url, marks: [{ type: 'link', attrs: { href: url } }] },
          { type: 'text', text: ' ' },
        ])
        .run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) setHref((editor.getAttributes('link').href as string | undefined) ?? '');
        setOpen(next);
      }}
    >
      <Tip tip="Add a link">
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Link"
            aria-pressed={active}
            onMouseDown={keepSelection}
            className={TOOL}
          >
            <Link2 />
          </button>
        </PopoverTrigger>
      </Tip>
      <PopoverContent align="start" className="w-72 p-2">
        <form
          className="flex items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            apply();
          }}
        >
          <Input
            value={href}
            aria-label="Link address"
            placeholder="Paste a link"
            onChange={(e) => setHref(e.target.value)}
            className="h-8 text-sm"
          />
          <Button type="submit" size="sm">
            Apply
          </Button>
          {active && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                editor.chain().focus().extendMarkRange('link').unsetLink().run();
                setOpen(false);
              }}
            >
              Remove
            </Button>
          )}
        </form>
      </PopoverContent>
    </Popover>
  );
}

function AttachButton({ accept, onPick }: { accept: string; onPick: (files: File[]) => void }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <Tip tip="Attach a file or image">
        <button
          type="button"
          aria-label="Attach"
          onMouseDown={keepSelection}
          onClick={() => input.current?.click()}
          className={TOOL}
        >
          <Paperclip />
        </button>
      </Tip>
      <input
        ref={input}
        type="file"
        multiple
        accept={accept}
        hidden
        onChange={(e) => {
          onPick([...(e.target.files ?? [])]);
          e.target.value = '';
        }}
      />
    </>
  );
}

const Divider = () => <span aria-hidden className="mx-0.5 h-4 w-px bg-border-faint" />;

export function RichTextToolbar({ editor, accept, onPickFiles }: RichTextToolbarProps) {
  return (
    <TooltipProvider delayDuration={300}>
      <div
        role="toolbar"
        aria-label="Formatting"
        className="flex flex-wrap items-center gap-0.5 border-b border-border-faint px-1 py-1"
      >
        <BlockTypeMenu editor={editor} />
        <Divider />
        {MARK_ACTIONS.map((a) => (
          <ToolButton key={a.label} action={a} editor={editor} />
        ))}
        <LinkMenu editor={editor} />
        <Divider />
        {LIST_ACTIONS.map((a) => (
          <ToolButton key={a.label} action={a} editor={editor} />
        ))}
        <Divider />
        {BLOCK_ACTIONS.map((a) => (
          <ToolButton key={a.label} action={a} editor={editor} />
        ))}
        {onPickFiles && (
          <>
            <Divider />
            <AttachButton accept={accept ?? ''} onPick={onPickFiles} />
          </>
        )}
      </div>
    </TooltipProvider>
  );
}
